import WidgetCRUDActions from '@mm/state/actions/WidgetCRUDActions';
import ButtonController from '@mm/state/controllers/ButtonController';
import AttributeModel from '@mm/state/model/AttributeModel';
import ButtonModel, { ButtonType } from '@mm/state/model/ButtonModel';
import Page, { PageKind } from '@mm/state/model/PageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Unwrap } from '@mm/utils';
import { maxBy } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { compose, withHandlers, withProps, withState } from 'recompose';
import { Dispatch } from 'redux';
import Textarea from '../../../../common/components/Textarea';
import Button from '../../../buttons/DeprecatedButton/DeprecatedButton';
import SelectorWithArrow from '../../SelectorWithArrow/SelectorWithArrow';
import { QUICK_QUESTION_TEXT, SAVE_TO_ATTR_TEXT } from './constants';
import './OptinQuickQuestion.scss';
import OptinQuickReply from './OptinQuickReply/OptinQuickReply';

const DEFAULT_ANSWER = 'Yes';

type PropsFromMapState0 = ReturnType<typeof mapStateToProps0>;
type PropsFromMapState = ReturnType<typeof mapStateToProps>;
type PropsFromWithProps = {
  quickReplies: ButtonModel[];
};

type PropsFromWithState = {
  quickQuestion: string;
  setQuickQuestion: (value: string) => string;
  selectedAttribute: AttributeModel;
  setSelectedAttribute: (selectedAttribute: AttributeModel) => {
    selectedAttribute;
  };
};

type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;

type InnerPropsBeforeWithHandlers = PropsFromWithProps &
  PropsFromWithState &
  PropsFromMapState0 &
  PropsFromMapState &
  PropsFromDispatch;

type PropsFromWithHandlers = Unwrap<typeof handlers>;

type InnerProps = InnerPropsBeforeWithHandlers & PropsFromWithHandlers;

type OuterProps = {
  qqWidget: WidgetModel;
  onRemoteWidgetUpdated: () => void;
};

type OwnProps = OuterProps & PropsFromWithState;

type Props = InnerProps & OuterProps;

const OptinQuickQuestion = ({
  pages,
  pageGroups,
  quickQuestion,
  setQuickQuestion,
  quickReplies,
  qqWidget,
  updateQuickQuestion,
  createAnswer,
  deleteAnswer,
  onRemoteWidgetUpdated,
  attributes,
  onChangeAttribute,
  busy,
  selectedAttribute,
}: Props) => {
  return (
    <div>
      <div className="optin-page-builder__title">{QUICK_QUESTION_TEXT}</div>
      <Textarea
        value={quickQuestion}
        className="optin-quick-question__textarea"
        onChange={e => setQuickQuestion(e.target.value)}
        onBlur={updateQuickQuestion}
        onMouseLeave={updateQuickQuestion}
        maxLength={150}
      />
      <div className="optin-page-builder__title">{SAVE_TO_ATTR_TEXT}</div>
      {selectedAttribute && (
        <SelectorWithArrow
          selectedItem={{ ...selectedAttribute, id: selectedAttribute.id }}
          onChange={onChangeAttribute}
          disabled={busy}
          items={attributes}
        />
      )}
      <div className="optin-quick-question__rows-container">
        {quickReplies &&
          quickReplies.map((q, i) => (
            <OptinQuickReply
              key={q.id}
              order={i + 1}
              quickReply={q}
              pages={pages}
              pageGroups={pageGroups}
              widgetId={qqWidget.id}
              onUpdate={onRemoteWidgetUpdated}
              onDelete={deleteAnswer}
            />
          ))}
        <div className="optin-quick-question__answer-btn">
          {quickReplies && quickReplies.length < 10 && (
            <Button invert blue onClick={() => createAnswer(pages)}>
              + Add Answer
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps0 = (state: StoreState) => ({
  attributes: state.attribute.entities.filter(a => !a.system),
});

const mapStateToProps = (state: StoreState) => ({
  chatbotId: activeChatbotIdSelector(state),
  pages: state.page.dropdown,
  pageGroups: state.pageGroup.entities,
  busy: state.widget.busy,
});

const mapDispatchToProps = (
  dispatch: Dispatch<StoreState>,
  ownProps: OwnProps,
) => ({
  updateQuickQuestion: async () => {
    const {
      qqWidget: {
        config: { body: prevQuickQuestion, variableId: prevVariableId },
      },
      quickQuestion,
      qqWidget,
      onRemoteWidgetUpdated,
      selectedAttribute,
    } = ownProps;

    if (
      quickQuestion !== prevQuickQuestion ||
      (selectedAttribute && selectedAttribute.id !== prevVariableId)
    ) {
      const nextConfig = Object.assign({}, qqWidget.config, {
        body: quickQuestion,
        variableId: selectedAttribute
          ? selectedAttribute.id
          : qqWidget.config.variableId,
      });
      const nextWidget = Object.assign({}, qqWidget, { config: nextConfig });

      await dispatch(WidgetCRUDActions.update(nextWidget));
      onRemoteWidgetUpdated();
    }
  },
  createAnswer: async (pages: Page[]) => {
    const {
      qqWidget: { id: widgetId, buttons },
      onRemoteWidgetUpdated,
    } = ownProps;
    const welcomePage = pages.find(page => page.kind === PageKind.WelcomePage);
    const lastButton = maxBy(buttons, b => b.sortOrder);
    const sortOrder =
      lastButton && lastButton.sortOrder ? lastButton.sortOrder + 1 : 1;
    const pageId = welcomePage && welcomePage.id;
    const answer = {
      name: DEFAULT_ANSWER,
      type: ButtonType.PAGE,
      sortOrder,
      config: { pageId },
    };

    await ButtonController.postButton(widgetId, answer);
    onRemoteWidgetUpdated();
  },
  deleteAnswer: async (buttonId: number) => {
    const {
      qqWidget: { id: widgetId },
      onRemoteWidgetUpdated,
    } = ownProps;

    await ButtonController.deleteButton(widgetId, buttonId);
    onRemoteWidgetUpdated();
  },
});

const onChangeAttribute =
  ({
    attributes,
    setSelectedAttribute,
    updateQuickQuestion,
  }: InnerPropsBeforeWithHandlers & OuterProps) =>
  (id: number) => {
    const dropdownAttribute = attributes.find(variable => variable.id === id);

    if (dropdownAttribute) {
      setSelectedAttribute(dropdownAttribute);
    }

    updateQuickQuestion();
  };

const handlers = { onChangeAttribute };

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps0),
  withProps(({ qqWidget }) => ({ quickReplies: qqWidget && qqWidget.buttons })),
  withState(
    'quickQuestion',
    'setQuickQuestion',
    ({ qqWidget }) => qqWidget && qqWidget.config && qqWidget.config.body,
  ),
  withState(
    'selectedAttribute',
    'setSelectedAttribute',
    ({ qqWidget, attributes }) =>
      qqWidget &&
      qqWidget.config &&
      qqWidget.config.variableId &&
      attributes &&
      attributes.find(
        (a: AttributeModel) => a.id === qqWidget.config.variableId,
      ),
  ),
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers(handlers),
);

export default enhance(OptinQuickQuestion);
